import type { UI } from '@/v2/types';
import { useCallback } from 'react';

import { CtaEvent } from '@shared/v2/components/createRoomOptionCTA/types';
import { useScrollIntoView } from '@shared/v2/components/InView';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import scrollTo from '@shared/v2/utils/scrollIntoView';

import useBreakpoints, { isScreen } from '@/hooks/useBreakpoints';
import { useSearchPanelControls } from '@/v2/components/pages/PackageViewV2/contexts/SearchPanelContext';
import { roomCtaDataLayerEvent } from '@/v2/components/pages/PackageViewV2/utils/dataLayer';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { dataLayer } from '@/v2/utils/dataLayer';

export type OnCtaClickOpts = {
  roomOption?: UI.RoomOption | undefined;
  roomIndex?: number;
  ctaText?: string;
  ctaVariant?: string;
  shouldScrollIntoView?: boolean;
};

const useCtaOnClick = (setPackageOptionsOpen: () => void) => {
  const shouldUseNewEvents = useGA4Events();
  const scrollIntoView = useScrollIntoView();
  const { openCalendar, openTravellers, openMobileSearchPanelModal } =
    useSearchPanelControls();
  const breakpoints = useBreakpoints();
  const smallScreen = isScreen(breakpoints)('xs', 'sm');

  const { setSearchQuery } = useSearchQuery();
  return useCallback(
    (
      event: CtaEvent,
      {
        roomOption,
        ctaText,
        ctaVariant,
        roomIndex,
        shouldScrollIntoView = true,
      }: OnCtaClickOpts,
    ) => {
      if (ctaText && roomIndex) {
        roomCtaDataLayerEvent({ action: ctaText, roomIndex });
      }

      if (
        shouldUseNewEvents &&
        event !== 'SELECT_ROOM' &&
        event !== 'CHANGE_DATES'
      ) {
        dataLayer.ctaClickEvent({
          itemText: ctaText || '',
          itemVariant: ctaVariant,
        });
      }
      switch (event) {
        case 'SELECT_ROOM':
          setSearchQuery({ room: roomOption?.roomType.id });
          return;
        case 'CHANGE_DATES':
          if (smallScreen) {
            openMobileSearchPanelModal();
          } else if (shouldScrollIntoView) {
            scrollTo('#date-range', { offset: -70, behavior: 'smooth' });
          }
          return openCalendar(roomOption?.roomType.id);
        case 'CHANGE_TRAVELLERS':
          if (smallScreen) {
            openMobileSearchPanelModal();
            openTravellers();
            return;
          } else if (shouldScrollIntoView) {
            scrollTo('#travellers', { offset: -70, behavior: 'smooth' });
          }
          return openTravellers();
        case 'CHANGE_PACKAGE_OPTION':
          setPackageOptionsOpen();
          if (shouldScrollIntoView) {
            scrollIntoView('package', { behavior: 'smooth' });
          }
          return;
        default:
      }
    },
    [
      openCalendar,
      openMobileSearchPanelModal,
      openTravellers,
      scrollIntoView,
      setPackageOptionsOpen,
      setSearchQuery,
      smallScreen,
      shouldUseNewEvents,
    ],
  );
};

export default useCtaOnClick;
